import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';

import { DownOutlined, UpOutlined, SaveOutlined, SettingOutlined, DeleteOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import styled, { keyframes, css } from 'styled-components';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.12);
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Count = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  background: rgba(14, 116, 144, 0.1);
  padding: 2px 10px;
  border-radius: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

// 包住 disabled <button> 讓 Antd Tooltip 能在 hover 時觸發（原生 disabled button 會吃掉 pointer event）
const TooltipTrigger = styled.span`
  display: inline-flex;
`;

const ActionButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: ${props => {
    if (props.$variant === 'primary') return '#0e7490';
    if (props.$variant === 'danger') return '#dc2626';
    return '#f97316';
  }};
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => {
    if (props.$variant === 'primary') return '#155e75';
    if (props.$variant === 'danger') return '#b91c1c';
    return '#ea580c';
  }};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
  }
`;

const ParcelsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const highlightPulse = keyframes`
  0% {
    background: rgba(251, 191, 36, 0.2);
    border-color: rgba(251, 191, 36, 0.8);
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.3);
  }
  50% {
    background: rgba(251, 191, 36, 0.15);
    border-color: rgba(251, 191, 36, 0.6);
    box-shadow: 0 0 0 8px rgba(251, 191, 36, 0.2);
  }
  100% {
    background: rgba(251, 191, 36, 0.1);
    border-color: rgba(251, 191, 36, 0.5);
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.15);
  }
`;

const ParcelCard = styled.div`
  background: ${props => props.$highlighted ? 'rgba(251, 191, 36, 0.1)' : props.$active ? 'rgba(14, 116, 144, 0.08)' : '#ffffff'};
  border: 2px solid ${props => props.$highlighted ? 'rgba(251, 191, 36, 0.5)' : props.$active ? '#0e7490' : 'rgba(15, 23, 42, 0.12)'};
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s ease;
  scroll-margin-top: 20px;

  ${props => props.$highlighted && css`
    animation: ${highlightPulse} 1.5s ease-in-out infinite;
  `}

  &:hover {
    border-color: ${props => props.$highlighted ? 'rgba(251, 191, 36, 0.6)' : props.$active ? '#0e7490' : 'rgba(14, 116, 144, 0.3)'};
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  }
`;

const ParcelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  user-select: none;
`;

const ParcelInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  cursor: pointer;
`;

const ParcelNumber = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$active ? '#0e7490' : '#1e293b'};
`;

const ItemCount = styled.div`
  font-size: 12px;
  color: #64748b;
  background: rgba(15, 23, 42, 0.06);
  padding: 2px 8px;
  border-radius: 6px;
`;

const ParcelActions = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const IconButton = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 4px;

  &:hover {
    background: rgba(15, 23, 42, 0.08);
    color: ${props => props.$variant === 'primary' ? '#0e7490' : '#64748b'};
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ParcelDetails = styled.div`
  padding: 0 16px 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 100px;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(15, 23, 42, 0.03);
  border-radius: 6px;
  font-size: 13px;
`;

const ItemSku = styled.div`
  font-weight: 500;
  color: #1e293b;
  font-family: monospace;
`;

const ItemQuantity = styled.div`
  color: #0e7490;
  font-weight: 600;
  text-align: center;
`;

const ItemBatch = styled.div`
  color: #64748b;
  font-size: 12px;
  text-align: right;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
`;

const ParcelsList = forwardRef(({
  parcels = [],
  currentParcelIndex,
  highlightedParcelIndex = null,
  onSwitchParcel,
  onDoParcel,
  onManageParcel,
  onClearAllParcels,
  isParcelDisabled = false,
  isFulfillmentLocked = false
}, ref) => {
  const { t } = useTranslation();
  const [expandedIndexes, setExpandedIndexes] = useState(new Set([currentParcelIndex]));
  const parcelRefs = useRef({});

  // 暴露 scrollToParcel 方法給父組件
  useImperativeHandle(ref, () => ({
    scrollToParcel: (index) => {
      const parcelElement = parcelRefs.current[index];
      if (parcelElement) {
        parcelElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // 自動展開該 parcel
        setExpandedIndexes(prev => {
          const newSet = new Set(prev);
          newSet.add(index);
          return newSet;
        });
      }
    }
  }));

  const toggleExpand = (event, index) => {
    event.stopPropagation();
    setExpandedIndexes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleParcelSelect = (index) => {
    if (onSwitchParcel) {
      onSwitchParcel(index);
    }
  };

  return (
    <Container>
      <Header>
        <Title>
          {t('operator.qc.parcels_list.title', { defaultValue: '箱子列表' })}
          <Count>{parcels.length}</Count>
        </Title>
        <ButtonGroup>
          <Tooltip
            title={isFulfillmentLocked ? t('operator.qc.parcels_list.locked_hint', { defaultValue: '出貨已完成，無法修改箱子' }) : ''}
          >
            {/* Tooltip 不會在原生 disabled <button> 上觸發 hover，需用 span 接 pointer event */}
            <TooltipTrigger>
              <ActionButton
                $variant="danger"
                onClick={onClearAllParcels}
                disabled={parcels.length === 0 || isFulfillmentLocked}
              >
                <DeleteOutlined />
                {t('operator.qc.parcels_list.clear_all', { defaultValue: '清除所有' })}
              </ActionButton>
            </TooltipTrigger>
          </Tooltip>
          <Tooltip
            title={isFulfillmentLocked ? t('operator.qc.parcels_list.locked_hint', { defaultValue: '出貨已完成，無法修改箱子' }) : ''}
          >
            <TooltipTrigger>
              <ActionButton
                $variant="warning"
                onClick={onDoParcel}
                disabled={isParcelDisabled || isFulfillmentLocked}
              >
                <SaveOutlined />
                {t('operator.qc.parcels_list.do_parcel', { defaultValue: '裝箱' })}
              </ActionButton>
            </TooltipTrigger>
          </Tooltip>
        </ButtonGroup>
      </Header>

      <ParcelsWrapper>
        {parcels.length === 0 ? (
          <EmptyState>
            {t('operator.qc.parcels_list.empty', { defaultValue: '尚未建立箱子' })}
          </EmptyState>
        ) : (
          parcels.map((parcel, index) => {
            const isActive = index === currentParcelIndex;
            const isExpanded = expandedIndexes.has(index);
            const itemCount = parcel.parcel_items?.length || 0;
            const totalQuantity = parcel.parcel_items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

            const isHighlighted = index === highlightedParcelIndex;

            return (
              <ParcelCard
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                ref={(el) => { parcelRefs.current[index] = el; }}
                $active={isActive}
                $highlighted={isHighlighted}
              >
                <ParcelHeader>
                  <ParcelInfo onClick={() => handleParcelSelect(index)}>
                    <ParcelNumber $active={isActive}>
                      {t('operator.qc.parcels_list.parcel_number', { number: index + 1, defaultValue: `箱子 ${index + 1}` })}
                    </ParcelNumber>
                    <ItemCount>
                      {t('operator.qc.parcels_list.item_count', { count: itemCount, total: totalQuantity, defaultValue: `${itemCount} 項 / ${totalQuantity} 件` })}
                    </ItemCount>
                  </ParcelInfo>
                  <ParcelActions>
                    <Tooltip
                      title={
                        isFulfillmentLocked
                          ? t('operator.qc.parcels_list.locked_hint', { defaultValue: '出貨已完成，無法修改箱子' })
                          : isHighlighted
                            ? t('operator.qc.parcels_list.click_to_manage_hint', { defaultValue: '點擊這裡管理箱子內的序號' })
                            : ''
                      }
                      open={isFulfillmentLocked ? undefined : isHighlighted}
                      color={isFulfillmentLocked ? undefined : 'orange'}
                    >
                      <TooltipTrigger>
                        <IconButton
                          $variant="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onManageParcel(index);
                          }}
                          disabled={itemCount === 0 || isFulfillmentLocked}
                          title={t('operator.qc.parcels_list.manage', { defaultValue: '管理' })}
                        >
                          <SettingOutlined />
                        </IconButton>
                      </TooltipTrigger>
                    </Tooltip>
                    <IconButton onClick={(e) => toggleExpand(e, index)}>
                      {isExpanded ? <UpOutlined /> : <DownOutlined />}
                    </IconButton>
                  </ParcelActions>
                </ParcelHeader>

                {isExpanded && (
                  <ParcelDetails>
                    {itemCount === 0 ? (
                      <EmptyState style={{ padding: '12px' }}>
                        {t('operator.qc.parcels_list.no_items', { defaultValue: '尚未加入商品' })}
                      </EmptyState>
                    ) : (
                      parcel.parcel_items.map((item, itemIndex) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <ItemRow key={itemIndex}>
                          <ItemSku>{item.product_sku || '--'}</ItemSku>
                          <ItemQuantity>x{item.quantity}</ItemQuantity>
                          <ItemBatch>{item.batch || '--'}</ItemBatch>
                        </ItemRow>
                      ))
                    )}
                  </ParcelDetails>
                )}
              </ParcelCard>
            );
          })
        )}
      </ParcelsWrapper>
    </Container>
  );
});

ParcelsList.displayName = 'ParcelsList';

export default ParcelsList;
